import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { ApplyTimeline } from "../components/ApplyTimeline";

function CheckYourEmail({ email, verificationLink, onResend, resending }) {
  const navigate = useNavigate();
  return (
    <div data-testid="register-pending-verification" className="px-6 md:px-12 lg:px-24 py-24 max-w-xl mx-auto">
      <p className="label-eyebrow text-[#1E3A8A]">— Almost there</p>
      <h1 className="font-serif-display text-5xl tracking-tighter leading-none font-light mt-4">
        Check your email
      </h1>
      <p className="mt-6 text-[#333] leading-relaxed">
        We've sent a confirmation link to{" "}
        <span className="font-mono-aam text-[#1E3A8A]" data-testid="register-pending-email">{email}</span>.
        Click the link in that email to activate your account, then sign in.
      </p>

      <ApplyTimeline stage="submitted" />

      {verificationLink && (
        <div data-testid="dev-verification-link" className="mt-8 border border-dashed border-[#1E3A8A]/40 bg-[#F4EFE6]/40 p-5">
          <p className="label-eyebrow text-[#1E3A8A]">— Dev mode</p>
          <p className="text-sm text-[#333] mt-2">
            Email sending isn't configured in this environment. Use the link below to confirm directly:
          </p>
          <a
            href={verificationLink}
            className="block mt-3 break-all text-[#1E3A8A] underline font-mono-aam text-xs"
          >
            {verificationLink}
          </a>
        </div>
      )}

      <div className="mt-10 flex flex-wrap gap-3">
        <Button
          onClick={onResend}
          disabled={resending}
          data-testid="resend-verification-btn"
          className="rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white px-6 py-4 h-auto label-eyebrow"
        >
          {resending ? "Sending…" : "Resend confirmation email"}
        </Button>
        <Button
          variant="outline"
          onClick={() => navigate("/login")}
          data-testid="go-to-login-btn"
          className="rounded-none border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white px-6 py-4 h-auto label-eyebrow"
        >
          Go to sign in
        </Button>
      </div>

      <p className="mt-10 text-sm text-[#555]">
        Wrong address?{" "}
        <Link to="/register" className="text-[#111] underline hover:text-[#1E3A8A]">
          Register again
        </Link>
      </p>
    </div>
  );
}

export default function Register() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "buyer",
    bio: "",
    country: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [pending, setPending] = useState(null); // { email, verification_link }
  const [resending, setResending] = useState(false);
  const { register, resendVerification } = useAuth();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = await register(form);
      setPending({
        email: data.email,
        verification_link: data.verification_link || null,
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!pending?.email) return;
    setResending(true);
    try {
      await resendVerification(pending.email);
      toast.success("Confirmation email sent");
    } catch (_e) {
      toast.error("Could not resend right now. Try again in a moment.");
    } finally {
      setResending(false);
    }
  };

  if (pending) {
    return (
      <CheckYourEmail
        email={pending.email}
        verificationLink={pending.verification_link}
        onResend={handleResend}
        resending={resending}
      />
    );
  }

  return (
    <div data-testid="register-page" className="px-6 md:px-12 lg:px-24 py-24 max-w-xl mx-auto">
      <p className="label-eyebrow text-[#555]">— Apply to join</p>
      <h1 className="font-serif-display text-5xl tracking-tighter leading-none font-light mt-4">
        Become a member
      </h1>
      <p className="text-[#555] mt-4 max-w-md">
        Both collectors and artists complete the same short form. Each application is
        personally reviewed — see why below.
      </p>
      <form onSubmit={handleSubmit} className="mt-12 space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="label-eyebrow text-[#555]">Full name</label>
            <Input
              data-testid="register-name"
              required
              value={form.name}
              onChange={set("name")}
              className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-[#111]"
            />
          </div>
          <div>
            <label className="label-eyebrow text-[#555]">Email</label>
            <Input
              data-testid="register-email"
              type="email"
              required
              value={form.email}
              onChange={set("email")}
              className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-[#111]"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="label-eyebrow text-[#555]">Password</label>
            <Input
              data-testid="register-password"
              type="password"
              required
              minLength={6}
              value={form.password}
              onChange={set("password")}
              className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-[#111]"
            />
          </div>
          <div>
            <label className="label-eyebrow text-[#555]">I am a</label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger
                data-testid="register-role"
                className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 focus:ring-0"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="buyer">Collector / Buyer</SelectItem>
                <SelectItem value="artist">Artist</SelectItem>
                <SelectItem value="art_center">Art Hub / Collective</SelectItem>
                <SelectItem value="journalist">Journalist</SelectItem>
                <SelectItem value="storyteller">Storyteller</SelectItem>
                <SelectItem value="writer">Writer (general)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <label className="label-eyebrow text-[#555]">Country</label>
          <Input
            data-testid="register-country"
            value={form.country}
            onChange={set("country")}
            placeholder="e.g. Nigeria"
            className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-[#111]"
          />
        </div>
        {form.role === "artist" && (
          <div>
            <label className="label-eyebrow text-[#555]">Artist statement</label>
            <Textarea
              data-testid="register-bio"
              value={form.bio}
              onChange={set("bio")}
              rows={4}
              placeholder="Tell collectors about your practice…"
              className="mt-2 rounded-none border border-[#E5E0D8] bg-transparent focus-visible:ring-0 focus-visible:border-[#111]"
            />
          </div>
        )}
        {form.role === "art_center" && (
          <div>
            <label className="label-eyebrow text-[#555]">About the hub</label>
            <Textarea
              data-testid="register-center-bio"
              value={form.bio}
              onChange={set("bio")}
              rows={4}
              placeholder="Tell artists and collectors about your art hub — its mission, community, and the artists you represent…"
              className="mt-2 rounded-none border border-[#E5E0D8] bg-transparent focus-visible:ring-0 focus-visible:border-[#111]"
            />
          </div>
        )}
        {["writer", "journalist", "storyteller"].includes(form.role) && (
          <div>
            <label className="label-eyebrow text-[#555]">A short bio</label>
            <Textarea
              data-testid="register-writer-bio"
              value={form.bio}
              onChange={set("bio")}
              rows={4}
              placeholder="Tell us about your editorial background, beats you cover, links to past work…"
              className="mt-2 rounded-none border border-[#E5E0D8] bg-transparent focus-visible:ring-0 focus-visible:border-[#111]"
            />
          </div>
        )}
        <div data-testid="register-curated-note" className="border-l-2 border-[#F4C430] bg-[#F4EFE6]/40 px-4 py-3 flex items-start gap-3">
          <svg className="w-4 h-4 text-[#1E3A8A] flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 12l5 5 9-11" />
          </svg>
          <p className="text-sm text-[#333]">
            <span className="label-eyebrow text-[#1E3A8A]">Hand-reviewed · </span>
            Every account is personally vetted by our editors. You'll receive an email once your application is approved — usually within 24 hours.
          </p>
        </div>
        <Button
          type="submit"
          data-testid="register-submit"
          disabled={submitting}
          className="w-full rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white py-6 h-auto label-eyebrow"
        >
          {submitting ? "Submitting application…" : "Apply to join"}
        </Button>
      </form>
      <p className="mt-8 text-sm text-[#555]">
        Already a member?{" "}
        <Link to="/login" className="text-[#111] underline hover:text-[#1E3A8A]">Sign in</Link>
      </p>
    </div>
  );
}
